/**
 * Quiet PM plan-review sessions on project drawing PDFs (`planReviews` collection).
 */

import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  getDoc,
  onSnapshot,
  updateDoc,
  doc,
  Timestamp,
  type Unsubscribe,
  limit,
} from 'firebase/firestore';
import { db } from './firebase';
import type { CRMUser, EnhancedDeal, PdfAnnotation, PlanReview, ProjectFile, TimberProject } from '../types';
import { getAssignedDesigner } from './dealDesignerArchitect';
import { getProjectManagers } from './employeeDesignations';
import {
  buildPlanReviewDefaultNotifyUserIds,
  collectPlanReviewMentionUserIds,
} from './planReviewSummary';

const COLLECTION = 'planReviews';

function parseTimestamp(value: unknown): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  return undefined;
}

function fromFirestore(id: string, data: Record<string, unknown>): PlanReview {
  return {
    id,
    projectId: String(data.projectId || ''),
    documentId: String(data.documentId || ''),
    documentUrl: String(data.documentUrl || ''),
    streamId: typeof data.streamId === 'string' ? data.streamId : undefined,
    fileName: String(data.fileName || 'File'),
    status: (data.status as PlanReview['status']) || 'active',
    startedAt: parseTimestamp(data.startedAt) || new Date(),
    startedBy: String(data.startedBy || ''),
    startedByName: String(data.startedByName || ''),
    completedAt: parseTimestamp(data.completedAt),
    completedBy: typeof data.completedBy === 'string' ? data.completedBy : undefined,
    completedByName: typeof data.completedByName === 'string' ? data.completedByName : undefined,
    cancelledAt: parseTimestamp(data.cancelledAt),
    cancelledBy: typeof data.cancelledBy === 'string' ? data.cancelledBy : undefined,
    cancelledByName: typeof data.cancelledByName === 'string' ? data.cancelledByName : undefined,
    annotationIds: Array.isArray(data.annotationIds)
      ? (data.annotationIds as string[]).map(String)
      : undefined,
    markupStrokeCount:
      typeof data.markupStrokeCount === 'number' ? data.markupStrokeCount : undefined,
    summaryNote: typeof data.summaryNote === 'string' ? data.summaryNote : undefined,
    summaryNoteHtml:
      typeof data.summaryNoteHtml === 'string' ? data.summaryNoteHtml : undefined,
    notifyUserIds: Array.isArray(data.notifyUserIds)
      ? (data.notifyUserIds as string[]).map(String)
      : undefined,
    createdAt: parseTimestamp(data.createdAt) || new Date(),
    updatedAt: parseTimestamp(data.updatedAt) || new Date(),
  };
}

export async function getActivePlanReviewForDocument(
  documentId: string,
): Promise<PlanReview | null> {
  const docId = (documentId || '').trim();
  if (!docId) return null;
  const q = query(
    collection(db, COLLECTION),
    where('documentId', '==', docId),
    where('status', '==', 'active'),
    limit(1),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return fromFirestore(d.id, d.data() as Record<string, unknown>);
}

export function subscribeActivePlanReviewForDocument(
  documentId: string,
  callback: (review: PlanReview | null) => void,
): Unsubscribe {
  const docId = (documentId || '').trim();
  if (!docId) {
    callback(null);
    return () => {};
  }
  const q = query(
    collection(db, COLLECTION),
    where('documentId', '==', docId),
    where('status', '==', 'active'),
    limit(1),
  );
  return onSnapshot(
    q,
    (snap) => {
      if (snap.empty) {
        callback(null);
        return;
      }
      const d = snap.docs[0];
      callback(fromFirestore(d.id, d.data() as Record<string, unknown>));
    },
    (err) => {
      console.error('[subscribeActivePlanReviewForDocument]', err);
      callback(null);
    },
  );
}

export function subscribePlanReviewsForDocument(
  documentId: string,
  callback: (reviews: PlanReview[]) => void,
): Unsubscribe {
  const docId = (documentId || '').trim();
  if (!docId) {
    callback([]);
    return () => {};
  }
  const q = query(collection(db, COLLECTION), where('documentId', '==', docId));
  return onSnapshot(
    q,
    (snap) => {
      const reviews = snap.docs
        .map((d) => fromFirestore(d.id, d.data() as Record<string, unknown>))
        .sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime());
      callback(reviews);
    },
    (err) => {
      console.error('[subscribePlanReviewsForDocument]', err);
      callback([]);
    },
  );
}

/** All plan reviews for a version track (active + completed + cancelled). */
export function subscribePlanReviewsForStream(
  streamId: string,
  callback: (reviews: PlanReview[]) => void,
): Unsubscribe {
  const sid = (streamId || '').trim();
  if (!sid) {
    callback([]);
    return () => {};
  }
  const q = query(collection(db, COLLECTION), where('streamId', '==', sid));
  return onSnapshot(
    q,
    (snap) => {
      const reviews = snap.docs
        .map((d) => fromFirestore(d.id, d.data() as Record<string, unknown>))
        .sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime());
      callback(reviews);
    },
    (err) => {
      console.error('[subscribePlanReviewsForStream]', err);
      callback([]);
    },
  );
}

export interface StartPlanReviewParams {
  projectId: string;
  documentId: string;
  documentUrl: string;
  streamId?: string;
  fileName: string;
  startedBy: string;
  startedByName: string;
}

export async function getPlanReviewById(reviewId: string): Promise<PlanReview | null> {
  const id = (reviewId || '').trim();
  if (!id) return null;
  const snap = await getDoc(doc(db, COLLECTION, id));
  if (!snap.exists()) return null;
  return fromFirestore(snap.id, snap.data() as Record<string, unknown>);
}

export async function startPlanReview(params: StartPlanReviewParams): Promise<string> {
  const existing = await getActivePlanReviewForDocument(params.documentId);
  if (existing?.id) {
    throw new Error('A plan review is already in progress for this drawing.');
  }
  const now = Timestamp.now();
  const ref = await addDoc(collection(db, COLLECTION), {
    projectId: params.projectId,
    documentId: params.documentId,
    documentUrl: params.documentUrl,
    ...(params.streamId ? { streamId: params.streamId } : {}),
    fileName: params.fileName,
    status: 'active',
    startedAt: now,
    startedBy: params.startedBy,
    startedByName: params.startedByName,
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

export async function cancelPlanReview(params: {
  reviewId: string;
  cancelledBy: string;
  cancelledByName: string;
}): Promise<void> {
  const now = Timestamp.now();
  await updateDoc(doc(db, COLLECTION, params.reviewId), {
    status: 'cancelled',
    cancelledAt: now,
    cancelledBy: params.cancelledBy,
    cancelledByName: params.cancelledByName,
    updatedAt: now,
  });
}

export async function completePlanReview(params: {
  reviewId: string;
  completedBy: string;
  completedByName: string;
  notifyUserIds: string[];
  summaryNote?: string;
  summaryNoteHtml?: string;
  annotationIds: string[];
  markupStrokeCount: number;
}): Promise<void> {
  const notifyUserIds = Array.from(
    new Set(params.notifyUserIds.map((id) => String(id).trim()).filter(Boolean)),
  );
  if (notifyUserIds.length === 0) {
    throw new Error('Select at least one recipient for the review summary.');
  }
  const now = Timestamp.now();
  const notePlain = params.summaryNote?.trim();
  const noteHtml = params.summaryNoteHtml?.trim();
  await updateDoc(doc(db, COLLECTION, params.reviewId), {
    status: 'completed',
    completedAt: now,
    completedBy: params.completedBy,
    completedByName: params.completedByName,
    notifyUserIds,
    ...(notePlain ? { summaryNote: notePlain } : {}),
    ...(noteHtml ? { summaryNoteHtml: noteHtml } : {}),
    annotationIds: params.annotationIds,
    markupStrokeCount: params.markupStrokeCount,
    updatedAt: now,
  });
}

export async function listAnnotationsForPlanReview(
  planReviewId: string,
): Promise<PdfAnnotation[]> {
  const reviewId = (planReviewId || '').trim();
  if (!reviewId) return [];
  const q = query(collection(db, 'pdfAnnotations'), where('planReviewId', '==', reviewId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      documentId: data.documentId as string,
      documentUrl: data.documentUrl as string,
      leadId: data.leadId as string,
      pageNumber: data.pageNumber as number,
      x: data.x as number,
      y: data.y as number,
      content: data.content as string,
      userId: data.userId as string,
      userName: data.userName as string,
      mentions: data.mentions as PdfAnnotation['mentions'],
      replies: ((data.replies as Array<Record<string, unknown>>) || []).map((r) => ({
        userId: String(r.userId || ''),
        userName: String(r.userName || ''),
        content: String(r.content || ''),
        mentions: r.mentions as PdfAnnotation['mentions'],
        createdAt:
          (r.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
      })),
      contextPreviewUrl:
        typeof data.contextPreviewUrl === 'string' ? data.contextPreviewUrl : undefined,
      planReviewId: typeof data.planReviewId === 'string' ? data.planReviewId : undefined,
      projectId: typeof data.projectId === 'string' ? data.projectId : undefined,
      createdAt: (data.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
    };
  });
}

export async function countMarkupStrokesForDocument(documentId: string): Promise<number> {
  const docId = (documentId || '').trim();
  if (!docId) return 0;
  const q = query(collection(db, 'documentMarkup'), where('documentId', '==', docId));
  const snap = await getDocs(q);
  return snap.size;
}

export function computePlanReviewDefaultRecipients(params: {
  file: Pick<ProjectFile, 'uploadedBy'> | null | undefined;
  project: Pick<TimberProject, 'projectManager' | 'assignedTo'> | null | undefined;
  deal: EnhancedDeal | null | undefined;
  crmUsers: CRMUser[];
  reviewAnnotations: PdfAnnotation[];
  excludeUserId?: string;
}): string[] {
  const designer = getAssignedDesigner(params.deal, params.crmUsers);
  const mentionIds = collectPlanReviewMentionUserIds(
    params.reviewAnnotations,
    params.excludeUserId,
  );
  const pmUsers = getProjectManagers(params.crmUsers);
  const projectManagerIds: string[] = [];
  if (params.project?.projectManager) {
    projectManagerIds.push(params.project.projectManager);
  }
  for (const pm of pmUsers) {
    if (
      params.project?.assignedTo?.includes(pm.uid) ||
      params.project?.projectManager === pm.uid
    ) {
      projectManagerIds.push(pm.uid);
    }
  }
  return buildPlanReviewDefaultNotifyUserIds({
    uploaderUserId: params.file?.uploadedBy,
    designerUserId: designer.uid,
    mentionUserIds: mentionIds,
    projectManagerUserIds: projectManagerIds,
    excludeUserId: params.excludeUserId,
  });
}
